import type { TimeInterval, WeeklySchedule } from '@acs/shared';

/**
 * Pure grid geometry & interval math (doc 10 §13). The primary unit-test
 * surface for the schedule editor: no React, no DOM. Every commit in the grid
 * and list editors flows through {@link normalizeDay} so stored intervals are
 * always sorted, non-overlapping and on the 15-minute snap — the exact
 * invariants the booking engine expects.
 */

export type GridInterval = TimeInterval; // { startMin, endMin }

export const SNAP_MIN = 15;
export const MIN_BLOCK_MIN = 15;
export const DAY_MIN = 1440;
/** Pixels per hour in the grid; `y(min) = (min - windowFrom) * HOUR_PX / 60`. */
export const HOUR_PX = 48;

/** Default column-header height. The hour axis is padded by exactly this, so
 * anything that changes the header's height must pass `headPx` to match. */
export const HEAD_PX = 24;

/** Header height for the dashboard's weekday + date column headings. Tall
 * enough for all three lines a closed day shows: name, date chip, "Closed". */
export const WEEK_HEAD_PX = 58;

/** Weekday data keys ('0'=Sun … '6'=Sat) in Monday-first DISPLAY order. */
export const DAY_KEY_BY_DISPLAY = ['1', '2', '3', '4', '5', '6', '0'] as const;

/** Round a minute-of-day to the snap grid, clamped to [0, 1440]. */
export function snap(min: number): number {
  return Math.max(0, Math.min(DAY_MIN, Math.round(min / SNAP_MIN) * SNAP_MIN));
}

/** Snap both ends, order them, and enforce the minimum block length, in-bounds. */
export function clampBlock(iv: GridInterval): GridInterval {
  let startMin = snap(iv.startMin);
  let endMin = snap(iv.endMin);
  if (endMin < startMin) [startMin, endMin] = [endMin, startMin];
  if (endMin - startMin < MIN_BLOCK_MIN) {
    endMin = Math.min(DAY_MIN, startMin + MIN_BLOCK_MIN);
    if (endMin - startMin < MIN_BLOCK_MIN) startMin = Math.max(0, endMin - MIN_BLOCK_MIN);
  }
  return { startMin, endMin };
}

/** Sort by start; merge overlapping AND touching (`a.end >= b.start`) intervals.
 * Snaps ends and drops zero/negative-length intervals — but does NOT inflate a
 * degenerate interval to the minimum block size (that is a drag-gesture concern,
 * handled by {@link clampBlock} at commit time). */
export function normalizeDay(ivs: GridInterval[]): GridInterval[] {
  const sorted = ivs
    .map((iv) => ({ startMin: snap(iv.startMin), endMin: snap(iv.endMin) }))
    .filter((iv) => iv.endMin > iv.startMin)
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const out: GridInterval[] = [];
  for (const iv of sorted) {
    const last = out[out.length - 1];
    if (last && iv.startMin <= last.endMin) last.endMin = Math.max(last.endMin, iv.endMin);
    else out.push({ ...iv });
  }
  return out;
}

/** "09:00" for 540. */
export function minToLabel(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** "9:30" for 570 — clock time without the leading zero, for places that read
 *  as a calendar rather than as a form field (the dashboard's booked slots). */
export function minToClock(min: number): string {
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
}

/** 540 for "09:00" (tolerates a blank/partial `<input type="time">`). */
export function labelToMin(label: string): number {
  const parts = label.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

function readDay(hours: WeeklySchedule['hours'], key: string): GridInterval[] {
  return (hours as Record<string, GridInterval[] | undefined>)[key] ?? [];
}

/** `WeeklySchedule.hours` → per-display-day interval arrays (Mon…Sun), normalized. */
export function weeklyToDays(hours: WeeklySchedule['hours']): GridInterval[][] {
  return DAY_KEY_BY_DISPLAY.map((key) => normalizeDay(readDay(hours, key).map((iv) => ({ ...iv }))));
}

/** Per-display-day arrays → `WeeklySchedule.hours`, omitting empty days. */
export function daysToWeekly(days: GridInterval[][]): WeeklySchedule['hours'] {
  const hours: Record<string, GridInterval[]> = {};
  days.forEach((ivs, i) => {
    const norm = normalizeDay(ivs);
    if (norm.length) hours[DAY_KEY_BY_DISPLAY[i]!] = norm;
  });
  return hours;
}

/** Side-by-side placement for possibly-overlapping intervals (appointments):
 * `lane` is the column slot, `laneCount` how many slots its overlap cluster
 * needs — so width = 1/laneCount. Non-overlapping intervals get the full width.
 * Result is index-aligned with the input. */
export function assignLanes(ivs: GridInterval[]): { lane: number; laneCount: number }[] {
  const out = ivs.map(() => ({ lane: 0, laneCount: 1 }));
  const order = ivs
    .map((_, i) => i)
    .sort((a, b) => ivs[a]!.startMin - ivs[b]!.startMin || ivs[a]!.endMin - ivs[b]!.endMin);

  const laneEnds: number[] = []; // per-lane end time within the current cluster
  let cluster: number[] = [];
  let clusterEnd = 0;
  const closeCluster = () => {
    const laneCount = laneEnds.length;
    for (const i of cluster) out[i]!.laneCount = laneCount;
    cluster = [];
    laneEnds.length = 0;
  };

  for (const i of order) {
    const iv = ivs[i]!;
    if (cluster.length && iv.startMin >= clusterEnd) closeCluster();
    let lane = laneEnds.findIndex((end) => end <= iv.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(iv.endMin);
    } else {
      laneEnds[lane] = iv.endMin;
    }
    out[i]!.lane = lane;
    cluster.push(i);
    clusterEnd = Math.max(clusterEnd, iv.endMin);
  }
  if (cluster.length) closeCluster();
  return out;
}

/**
 * The dashboard calendar's window (doc 43 D17): from the earliest to the latest
 * thing shown — working hours and bookings, across every visible day — snapped
 * out to whole hours, plus exactly `padMin` of empty space above and below.
 * Unlike {@link visibleWindow} there is no fixed 07:00–21:00 floor: every hour
 * on screen that holds nothing makes the bookings in the rest smaller. With
 * nothing at all to show (a closed week, no bookings) it uses `fallback`; a
 * window shorter than `minSpanMin` is grown to it.
 */
export function fitWindow(
  days: GridInterval[][],
  padMin = 60,
  fallback: { fromMin: number; toMin: number } = { fromMin: 480, toMin: 1080 },
  /** Shortest window shown: a lone 30-minute booking would otherwise fill the
   *  screen. Grown downwards (upwards at the end of the day). */
  minSpanMin = 240,
): { fromMin: number; toMin: number } {
  let from = Infinity;
  let to = -Infinity;
  for (const ivs of days) {
    for (const iv of ivs) {
      from = Math.min(from, Math.floor(iv.startMin / 60) * 60);
      to = Math.max(to, Math.ceil(iv.endMin / 60) * 60);
    }
  }
  if (!Number.isFinite(from) || !Number.isFinite(to)) return fallback;
  let fromMin = Math.max(0, from - padMin);
  let toMin = Math.min(DAY_MIN, to + padMin);
  if (toMin - fromMin < minSpanMin) {
    toMin = Math.min(DAY_MIN, fromMin + minSpanMin);
    fromMin = Math.max(0, toMin - minSpanMin);
  }
  return { fromMin, toMin };
}

/** The smallest [from,to] window (snapped to the hour) that shows `def` plus
 * every interval in `days`, so a block outside the default window is visible. */
export function visibleWindow(
  days: GridInterval[][],
  def: { fromMin: number; toMin: number } = { fromMin: 420, toMin: 1260 },
): { fromMin: number; toMin: number } {
  let { fromMin, toMin } = def;
  for (const ivs of days) {
    for (const iv of ivs) {
      fromMin = Math.min(fromMin, Math.floor(iv.startMin / 60) * 60);
      toMin = Math.max(toMin, Math.ceil(iv.endMin / 60) * 60);
    }
  }
  return { fromMin: Math.max(0, fromMin), toMin: Math.min(DAY_MIN, toMin) };
}
